import { ref } from 'vue'
import Pusher from 'pusher-js'
import env from '~/libs/env'
import { useBorrowerStore } from '~/store/borrower'
import { useUserStore } from '~/store/user'

let pusherInstance
let BorrowerChannel

// Incremented only when connect() actually produces a new BorrowerChannel (not on every call -
// a call that bails early, e.g. a missing auth header, must not bump this). Consumers (see
// composables/useStatementPullStatus.ts) watch this instead of borrowerId directly: borrowerId
// is written synchronously inside store/borrower.js's setBorrower(), well before
// middleware/020.pusher.global.js's disconnect()/connect() cycle actually runs on the next
// navigation, so a borrowerId-driven watcher fires and rebinds to the *old*, about-to-be-torn-
// down channel. Watching this generation instead means the rebind can only ever happen after the
// new channel genuinely exists.
const channelGeneration = ref(0)

export default {
  connect () {
    const pusherKey = env('pusherKey')
    const userStore = useUserStore()
    const borrowerStore = useBorrowerStore()

    // Only logged-in users get to connect
    if (!userStore.getAuthHeader() || !pusherKey || !borrowerStore.borrowerId) {
      return false
    }

    const apiUrl = env('apiUrl')
    const { $axios } = useNuxtApp()

    // Connect to pusher itself
    pusherInstance = new Pusher(pusherKey, {
      cluster: env('pusherCluster'),
      channelAuthorization: {
        customHandler: async (params, callback) => {
          try {
            const response = await $axios.post(`${apiUrl}/authorization/pusher`, {
              socket_id: params.socketId,
              channel_name: params.channelName,
            })
            callback(null, response.data)
          } catch (e) {
            callback(e, null)
          }
        },
      },
    })

    const borrowerChannelName = `private-borrower-${borrowerStore.borrowerId}-${env('environment')}-notification-channel`
    BorrowerChannel = pusherInstance.subscribe(borrowerChannelName)
    channelGeneration.value += 1

    return pusherInstance
  },

  getInstance () {
    return pusherInstance || this.connect()
  },

  getBorrowerChannel () {
    return BorrowerChannel
  },

  getChannelGeneration () {
    return channelGeneration.value
  },

  disconnect () {
    if (BorrowerChannel) {
      BorrowerChannel.unbind_all()
      BorrowerChannel = undefined
    }

    if (pusherInstance) {
      pusherInstance.disconnect()
      pusherInstance = undefined
    }
  }
}